import type { ColorToolScope } from "../types";
import { assertNever } from "./assertNever";

/** Radio-option labels for a `ColorToolScope` picker — shared by
 * ReplaceColorsModal and NonBrandModal so both scope pickers show identical
 * option text instead of each declaring their own copy of the same map. */
export const SCOPE_OPTION_LABELS: Record<ColorToolScope, string> = {
  selected: "Selected Shapes",
  current: "Current Slide",
  range: "Slide Range",
  all: "All Slides",
};

/** The scope-driven action options every Colour Tools scope picker resolves
 * to — shared shape for detectNonBrandColours/detectColoursInScope/replaceColours. */
export interface ResolvedColorToolScope {
  slideFrom?: number;
  slideTo?: number;
  currentSlideOnly?: boolean;
  selectedShapesOnly?: boolean;
}

/** Turns a `ColorToolScope` radio choice into the slideFrom/slideTo/
 * currentSlideOnly/selectedShapesOnly options colourOps' scope-driven
 * functions expect — shared by ReplaceColorsModal and NonBrandModal so both
 * scope pickers resolve identically instead of each re-deriving it. */
export function resolveColorToolScope(
  scope: ColorToolScope,
  rangeFrom: number,
  rangeTo: number,
): ResolvedColorToolScope {
  switch (scope) {
    case "range":
      return { slideFrom: rangeFrom, slideTo: rangeTo };
    case "current":
      return { currentSlideOnly: true };
    case "selected":
      return { selectedShapesOnly: true };
    case "all":
      return {};
    default:
      return assertNever(scope);
  }
}

/** Human-readable description of a `ColorToolScope` for status/summary text.
 * `from`/`to` are only used for the "range" case; omit them to fall back to
 * the generic "slide range" label. */
export function describeColorToolScopeLabel(
  scope: ColorToolScope,
  from?: number,
  to?: number,
): string {
  switch (scope) {
    case "selected":
      return "selected shapes";
    case "current":
      return "current slide";
    case "all":
      return "all slides";
    case "range":
      return from != null && to != null ? `slides ${from}–${to}` : "slide range";
    default:
      return assertNever(scope);
  }
}
